// File: src/pages/Invoices/InvoiceApprovalPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { CheckCircle, XCircle, Eye, Loader, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { useUserPermissions } from "@/hooks/useUserPermissions";

/* ------------------------------- Utils ---------------------------------- */
const currency = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const toStr = (v) => (v == null ? "" : String(v));
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Try to pick a sensible invoice amount.
 * We intentionally skip 0 values so auto-generated invoices
 * that store 0 in one field but the real amount in another
 * still show correctly.
 */
function pickAmount(inv) {
  const keysInPriority = ["amount_due", "total_amount", "total", "amount"];
  for (const key of keysInPriority) {
    if (inv?.[key] == null) continue;
    const num = Number(inv[key]);
    if (Number.isFinite(num) && num !== 0) return num;
  }
  const fallback = Number(inv?.amount || inv?.total || inv?.amount_due || 0);
  return Number.isFinite(fallback) ? fallback : 0;
}

/* Accept API rows whether they use amount, amount_due, total, etc.
   Also tolerate payload shapes: [] or { invoices: [...] } or { rows: [...] } */
function normalizePendingRows(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.invoices)
      ? payload.invoices
      : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

  return rows
    .filter(Boolean)
    .map((inv) => {
      const amountNormalized = pickAmount(inv);
      const vendorNameNormalized =
        inv.vendor_name ??
        inv.vendor ??
        inv.vendor_company ??
        inv.supplier_name ??
        null;

      return {
        ...inv,
        amount: amountNormalized,
        vendor_name: vendorNameNormalized,
      };
    });
}

/**
 * Build a file URL that points to the API host (not the Vercel UI host).
 * - If file_url is absolute => use as-is
 * - If file_url is relative => prefix with API origin
 * - Else if filename exists => /uploads/invoices/<filename> on API origin
 */
function buildInvoiceFileHref(inv) {
  const fileUrl = toStr(inv?.file_url || inv?.fileUrl || "");
  const filename = toStr(inv?.filename || inv?.file_name || inv?.stored_filename || "");

  const rawBase = toStr(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  const apiOrigin = rawBase.replace(/\/solo-mdsh-api\/?$/i, "");

  const join = (base, path) => {
    const b = toStr(base).replace(/\/+$/, "");
    const p = toStr(path).startsWith("/") ? toStr(path) : `/${toStr(path)}`;
    return `${b}${p}`;
  };

  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

  if (fileUrl) {
    if (fileUrl.startsWith("/")) return apiOrigin ? join(apiOrigin, fileUrl) : fileUrl;
    return apiOrigin ? join(apiOrigin, `/${fileUrl}`) : `/${fileUrl}`;
  }

  if (filename) {
    const rel = `/uploads/invoices/${encodeURIComponent(filename)}`;
    return apiOrigin ? join(apiOrigin, rel) : rel;
  }

  return null;
}

/* ---------------------------- Component --------------------------------- */
export default function InvoiceApprovalPage() {
  const navigate = useNavigate();

  // ✅ Use the app’s canonical permission source (AuthProvider + merged roles)
  const { isAdmin, isDeveloper, isFinance, isManager, isProcurement } = useUserPermissions();

  // View: finance/procurement/admin/dev/manager
  const canView = Boolean(isAdmin || isDeveloper || isFinance || isManager || isProcurement);

  // Decide: admin/dev/finance/manager
  const canDecide = Boolean(isAdmin || isDeveloper || isFinance || isManager);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // inline View modal

  const fetchPending = async () => {
    setLoading(true);
    setError(null);

    // IMPORTANT: don’t hardcode "/solo-mdsh-api" — axios base handles it.
    const endpoints = [
      "/procurement/invoices/pending",
      "/invoices/pending",
      "/procurement/invoices/pending?status=pending",
      "/invoices/pending?status=pending",
    ];

    let lastErr = null;

    for (const url of endpoints) {
      try {
        const res = await api.get(url);
        setInvoices(normalizePendingRows(res?.data));
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        if (e?.response?.status === 404) continue;
        break;
      }
    }

    if (lastErr) {
      console.error("❌ Error fetching pending invoices:", lastErr);
      if (lastErr?.response?.status === 403) navigate("/forbidden");
      setInvoices([]);
      setError(lastErr?.response?.data?.error || "Failed to fetch pending invoices");
    }

    setLoading(false);
  };

  useEffect(() => {
    // If user can’t view, route guard SHOULD stop them, but keep a safe fallback:
    if (!canView) {
      navigate("/forbidden");
      return;
    }
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleDecision = async (id, decision) => {
    if (!canDecide) {
      toast.error("You don't have permission to perform this action.");
      return;
    }

    const actionLabel = decision === "approve" ? "Approving" : "Rejecting";
    const pastTense = decision === "approve" ? "approved" : "rejected";

    const comment = prompt(`Add a comment for this ${decision.toUpperCase()} decision:`);
    if (comment === null) return;

    setProcessingId(id);
    const toastId = toast.loading(`${actionLabel} invoice...`);

    try {
      const route = decision === "approve" ? "approve" : "reject";
      const endpoints = [
        `/procurement/invoices/${route}`,
        `/invoices/${route}`,
      ];

      let ok = false;
      let lastErr = null;

      for (const url of endpoints) {
        try {
          await api.post(url, { invoice_id: id, comment });
          ok = true;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (e?.response?.status === 404) continue;
          break;
        }
      }

      if (!ok) throw lastErr || new Error("No working approve/reject endpoint found.");

      setInvoices((prev) => prev.filter((inv) => (inv.invoice_id ?? inv.id) !== id));
      toast.success(`Invoice ${id} ${pastTense} successfully!`, { id: toastId });
    } catch (err) {
      console.error(`❌ Failed to ${decision} invoice:`, err);
      if (err?.response?.status === 403) navigate("/forbidden");
      toast.error(`Failed to ${decision} invoice.`, { id: toastId });
    } finally {
      setProcessingId(null);
    }
  };

  const totalAmount = useMemo(
    () => invoices.reduce((sum, inv) => sum + toNum(inv.amount || 0), 0),
    [invoices]
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-300">
        <Loader className="animate-spin w-4 h-4" /> Loading invoices...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 text-white">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">📥 Pending Invoices for Approval</h1>
          <p className="text-sm text-gray-400">
            Total Pending Amount:{" "}
            <span className="text-white font-bold">{currency(totalAmount)}</span>
          </p>
          {!canDecide && (
            <div className="mt-1 text-xs text-amber-300">
              You have view-only access. Ask an admin to grant approval rights if needed.
            </div>
          )}
        </div>

        <button
          onClick={fetchPending}
          className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded inline-flex items-center gap-2"
          type="button"
        >
          <Loader className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {invoices.length === 0 ? (
        <p className="text-gray-400">No pending invoices to approve.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-[#1b1b1b] sticky top-0 z-10">
              <tr>
                <th className="border-b border-white/10 p-2 text-left">Invoice #</th>
                <th className="border-b border-white/10 p-2 text-left">Vendor</th>
                <th className="border-b border-white/10 p-2 text-left">PO</th>
                <th className="border-b border-white/10 p-2 text-left">Amount</th>
                <th className="border-b border-white/10 p-2 text-left">Due</th>
                <th className="border-b border-white/10 p-2 text-left">Uploaded By</th>
                <th className="border-b border-white/10 p-2 text-left">Notes</th>
                <th className="border-b border-white/10 p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, idx) => {
                const id = inv.invoice_id ?? inv.id;
                const isOverdue = inv.due_date && dayjs().isAfter(dayjs(inv.due_date));

                return (
                  <tr
                    key={toStr(id)}
                    className={idx % 2 === 0 ? "bg-[#141414]" : "bg-[#101010]"}
                  >
                    <td className="p-2">{inv.invoice_number || `#${id}`}</td>
                    <td className="p-2">{inv.vendor_name || "—"}</td>
                    <td className="p-2">{inv.po_id ?? "—"}</td>
                    <td className="p-2">{currency(inv.amount)}</td>
                    <td className={`p-2 ${isOverdue ? "text-rose-400 font-semibold" : ""}`}>
                      {inv.due_date ? dayjs(inv.due_date).format("YYYY-MM-DD") : "—"}
                    </td>
                    <td className="p-2">{inv.uploaded_by || "—"}</td>
                    <td className="p-2 text-xs text-gray-400">{inv.notes || "—"}</td>
                    <td className="p-2">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setSelected(inv)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded inline-flex items-center gap-1"
                          type="button"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>

                        <button
                          onClick={() => navigate(`/invoice/review/${id}`)}
                          className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1 rounded inline-flex items-center gap-1"
                          type="button"
                        >
                          <FileText className="w-4 h-4" /> Line Review
                        </button>

                        <button
                          onClick={() => handleDecision(id, "approve")}
                          disabled={processingId === id || !canDecide}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                          title={!canDecide ? "View-only access" : "Approve invoice"}
                          type="button"
                        >
                          {processingId === id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}{" "}
                          Approve
                        </button>

                        <button
                          onClick={() => handleDecision(id, "reject")}
                          disabled={processingId === id || !canDecide}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                          title={!canDecide ? "View-only access" : "Reject invoice"}
                          type="button"
                        >
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== View Modal ===== */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white text-gray-900 w-full max-w-2xl rounded-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Invoice #{selected.invoice_number || selected.invoice_id || selected.id}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
                type="button"
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500">Vendor:</span> {selected.vendor_name || "—"}
                </div>
                <div>
                  <span className="text-gray-500">PO:</span> {selected.po_id ?? "—"}
                </div>
                <div>
                  <span className="text-gray-500">Amount:</span> {currency(selected.amount)}
                </div>
                <div>
                  <span className="text-gray-500">Due:</span>{" "}
                  {selected.due_date ? dayjs(selected.due_date).format("YYYY-MM-DD") : "—"}
                </div>
                <div>
                  <span className="text-gray-500">Uploaded By:</span> {selected.uploaded_by || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Uploaded At:</span>{" "}
                  {selected.uploaded_at ? dayjs(selected.uploaded_at).format("YYYY-MM-DD HH:mm") : "—"}
                </div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">Notes</div>
                <div className="text-gray-800">{selected.notes || "—"}</div>
              </div>

              {buildInvoiceFileHref(selected) ? (
                <a
                  href={buildInvoiceFileHref(selected)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
                >
                  Open Original File
                </a>
              ) : (
                <div className="text-xs text-gray-500">
                  No uploaded file for this invoice (auto-generated). You can still approve or reject it.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}